/**
 * Page Title Controller
 * Centralized browser tab title management for the web app
 *
 * Convention:
 * - Auth pages: "modcar | <name>"
 * - App pages: "modcar | app — <PageName>"
 * - Fallback: "modcar | app"
 */

/**
 * Auth routes (no "app —" prefix)
 * Format: modcar | <name>
 */
const AUTH_ROUTES: Record<string, string> = {
  '/login': 'login',
  '/signup': 'signup',
  '/signup/continue': 'signup',
  '/reset-password': 'forgot password',
  '/verify-email': 'verify email',
  '/invite': 'invite', // Prefix match for /invite/:id
  '/onboarding': 'onboarding', // Prefix match for /onboarding/*
};

/**
 * App routes (with "app —" prefix)
 * Format: modcar | app — <PageName>
 */
const APP_ROUTES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/vehicles': 'Vehicles',
  '/customers': 'Customers',
  '/purchase-invoices': 'Purchase invoices',
  '/sales-invoices': 'Sales invoices',
  '/pipeline': 'Pipeline',
  '/garage': 'Garage',
  '/inventory': 'Inventory',
  '/analytics': 'Analytics',
  '/calendar': 'Calendar',
  '/settings': 'Settings',
  '/support': 'Support',
  '/profile': 'Profile',
  '/admin/teams': 'Team Management',
};

/**
 * Check if a pathname matches an auth route
 * Supports exact matches and prefix matches
 */
function matchAuthRoute(pathname: string): string | null {
  // Exact match first
  if (AUTH_ROUTES[pathname]) {
    return AUTH_ROUTES[pathname];
  }

  // Prefix match for dynamic routes
  for (const [route, title] of Object.entries(AUTH_ROUTES)) {
    if (pathname.startsWith(route + '/') || pathname === route) {
      return title;
    }
  }

  return null;
}

/**
 * Check if a pathname matches an app route
 * Supports exact matches and prefix matches (for /vehicles/:id, etc.)
 */
function matchAppRoute(pathname: string): string | null {
  // Exact match first
  if (APP_ROUTES[pathname]) {
    return APP_ROUTES[pathname];
  }

  // Prefix match for dynamic routes (e.g., /vehicles/123 → Vehicles)
  for (const [route, title] of Object.entries(APP_ROUTES)) {
    if (pathname.startsWith(route + '/') || pathname.startsWith(route + '?')) {
      return title;
    }
  }

  return null;
}

/**
 * Get the page title for a given pathname
 */
export function getPageTitle(pathname: string): string {
  // Check auth routes first
  const authTitle = matchAuthRoute(pathname);
  if (authTitle) {
    return `modcar | ${authTitle}`;
  }

  // Check app routes
  const appTitle = matchAppRoute(pathname);
  if (appTitle) {
    return `modcar | app — ${appTitle}`;
  }

  // Fallback for unknown routes
  // If it looks like an auth route (starts with known auth prefixes), use auth format
  const authPrefixes = ['/login', '/signup', '/reset', '/verify', '/invite', '/onboarding'];
  if (authPrefixes.some((prefix) => pathname.startsWith(prefix))) {
    return 'modcar | login';
  }

  // Default fallback for app routes
  return 'modcar | app';
}

/**
 * Set the document title based on the current pathname
 */
export function setPageTitle(pathname: string): void {
  document.title = getPageTitle(pathname);
}
